import { supabase } from '@/integrations/supabase/client';
import type { GuestQuote } from '@/types/database';
import { toast } from 'sonner';
import { addToOfflineQueue } from '@/lib/offlineStorage';
import { cacheQuotes, getCachedQuotes } from '@/lib/offlineDataCache';
import { buildQuoteTotalsColumns, quoteItemLineTotal } from '@/lib/quoteTotals';
import { buildQuotePaymentColumns } from '@/lib/quotePaymentInfo';
import { buildQuoteWarrantyColumns } from '@/lib/quoteWarranty';
import { buildQuoteHeadingColumns } from '@/lib/quoteHeading';
import { buildQuoteExtraFieldsColumns, quoteExtraFieldsFromRow } from '@/lib/quoteExtraFields';
import { createUuid } from '@/lib/uuid';
import { getQuotePeriodStart } from '@/lib/subscriptionLimits';

// ---------------------------------------------------------------------------
// Guest ("visitor") quote import.
//
// A visitor can build and save ONE quote before creating an account. It lives
// in localStorage under GUEST_QUOTE_KEY. When they log in we offer to import it
// into their account (see GuestQuoteImportPrompt). This module holds the
// storage keys and the import/discard logic so both the prompt and any legacy
// caller share a single, correct implementation.
//
// Team accounts: quotes are company-scoped and RLS rejects any insert whose
// company_id the user can't write to (null included). So the import MUST carry
// a resolved company_id — this was the bug behind "visitor quotes not importing
// after login" once membership-based RLS shipped.
// ---------------------------------------------------------------------------

export const GUEST_QUOTE_KEY = 'orcazap_guest_quote';
export const GUEST_DRAFT_KEY = 'orcazap_draft_new';
export const VISITOR_QUOTE_USED_KEY = 'orcapro_visitor_quote_used';
/**
 * Stores the `created_at` of a guest quote the user chose to KEEP without
 * importing, so the prompt doesn't nag on every reload. A brand-new guest quote
 * (different created_at) will prompt again.
 */
export const IMPORT_DISMISSED_KEY = 'orcamigo_guest_quote_import_dismissed';

export function getGuestQuote(): GuestQuote | null {
  const raw = localStorage.getItem(GUEST_QUOTE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as GuestQuote;
  } catch (e) {
    console.error('[guestQuoteImport] Invalid guest quote JSON:', e);
    return null;
  }
}

export function hasGuestQuote(): boolean {
  return !!localStorage.getItem(GUEST_QUOTE_KEY);
}

/** True if the user already chose to keep THIS guest quote without importing. */
export function isImportDismissed(guest: GuestQuote | null): boolean {
  if (!guest) return false;
  const dismissed = localStorage.getItem(IMPORT_DISMISSED_KEY);
  return !!dismissed && dismissed === (guest.created_at || 'true');
}

/** Remember that the user wants to keep this guest quote but not import it. */
export function dismissImportForNow(guest: GuestQuote | null): void {
  localStorage.setItem(IMPORT_DISMISSED_KEY, guest?.created_at || 'true');
}

/** Permanently remove the local guest quote and any related flags/drafts. */
export function discardGuestQuote(): void {
  localStorage.removeItem(GUEST_QUOTE_KEY);
  localStorage.removeItem(VISITOR_QUOTE_USED_KEY);
  localStorage.removeItem(GUEST_DRAFT_KEY);
  localStorage.removeItem(IMPORT_DISMISSED_KEY);
  window.dispatchEvent(new CustomEvent('quotes-updated'));
}

/**
 * Resolve a company_id the user can write to for the imported quote.
 * Prefers the caller-supplied active company, then a company the user owns
 * (oldest first), then any writing membership. Mirrors resolveSyncCompanyId in
 * NetworkContext so online import and offline sync agree on the target company.
 */
export async function resolveImportCompanyId(
  userId: string,
  preferredCompanyId: string | null | undefined,
): Promise<string | null> {
  let resolved: string | null = preferredCompanyId ?? null;
  if (resolved && String(resolved).startsWith('offline_')) {
    resolved = null;
  }

  if (!resolved) {
    const { data: owned, error } = await supabase
      .from('companies')
      .select('id')
      .eq('user_id', userId)
      .order('created_at', { ascending: true })
      .limit(1)
      .maybeSingle();
    if (error) {
      console.warn('[guestQuoteImport] Error resolving owned company:', error);
    }
    resolved = (owned as { id?: string } | null)?.id ?? null;
  }

  if (!resolved) {
    const { data: membership, error } = await supabase
      .from('company_members')
      .select('company_id, created_at')
      .eq('user_id', userId)
      .in('role', ['owner', 'admin', 'member'])
      .order('created_at', { ascending: true })
      .limit(1)
      .maybeSingle();
    if (error) {
      console.warn('[guestQuoteImport] Error resolving membership company:', error);
    }
    resolved = (membership as { company_id?: string } | null)?.company_id ?? null;
  }

  return resolved;
}

interface ImportGuestQuoteArgs {
  userId: string;
  /** The active company id from CompanyContext, if any. */
  companyId: string | null;
}

/**
 * Import the local guest quote into the logged-in user's account.
 *
 * Returns true when the quote is safely persisted — either inserted online, or
 * queued to the offline sync layer (which resolves company_id again at sync).
 * On a recoverable RLS/session race it returns false WITHOUT removing the local
 * quote, so the caller can retry.
 */
export async function importGuestQuote({ userId, companyId }: ImportGuestQuoteArgs): Promise<boolean> {
  const guestQuote = getGuestQuote();
  if (!guestQuote) return false;

  if (!userId) {
    console.error('[importGuestQuote] userId is empty, cannot import');
    return false;
  }

  const safeStatus = (guestQuote.status || 'pending') as GuestQuote['status'];
  const safeClientName = guestQuote.client_name || 'Cliente não informado';
  const safeTitle = guestQuote.title?.trim() || null;

  // Resolve a writable company up front. Both the online insert and the offline
  // fallback need it (RLS rejects a null/foreign company_id).
  const resolvedCompanyId = await resolveImportCompanyId(userId, companyId);

  const clearLocal = () => {
    localStorage.removeItem(GUEST_QUOTE_KEY);
    localStorage.removeItem(VISITOR_QUOTE_USED_KEY);
    localStorage.removeItem(GUEST_DRAFT_KEY);
    localStorage.removeItem(IMPORT_DISMISSED_KEY);
  };

  const convertGuestQuoteToOffline = (): boolean => {
    try {
      const itemsData = (guestQuote.items || []).map((item, index) => ({
        id: item.id || createUuid(),
        name: item.name || 'Item sem nome',
        description: item.description || null,
        quantity: item.quantity || 1,
        unit_price: item.unit_price || 0,
        discount: item.discount || 0,
        discount_type: item.discount_type || 'value',
        item_type: item.itemType || 'service',
        unit: item.unit || null,
        // Só a foto 1: as fotos 2/3 são PRO (`isProUser` no card), e o
        // visitante que gera este rascunho nunca é premium — `GuestQuoteItem`
        // não carrega os dois campos porque não há como preenchê-los.
        image_url: item.image_url || null,
        // Mesma fórmula do resto do app (ignorava `discount_type` aqui).
        total: quoteItemLineTotal({ ...item, quantity: item.quantity || 1 }),
        position: index,
        source: 'manual',
        product_id: item.product_id || null,
      }));

      const quotePayload = {
        client_name: safeClientName,
        status: safeStatus,
        document_type: guestQuote.document_type || 'orcamento',
        validity_days: guestQuote.validity_days || null,
        ...buildQuoteWarrantyColumns(guestQuote),
        ...buildQuoteHeadingColumns({
          subtitle: guestQuote.subtitle ?? '',
          issuePlace: guestQuote.issue_place ?? '',
        }),
        ...buildQuoteExtraFieldsColumns({ extraFields: quoteExtraFieldsFromRow(guestQuote) }),
        notes: guestQuote.notes || null,
        terms_conditions: guestQuote.terms_conditions || null,
        // O visitante pode ter digitado frete/desconto antes de criar a conta.
        ...buildQuoteTotalsColumns({
          items: guestQuote.items || [],
          shipping: guestQuote.shipping ?? 0,
          discount: guestQuote.discount ?? 0,
          discountType: guestQuote.discount_type ?? 'value',
        }),
        // …e as formas aceitas / condições / referência que ele escolheu antes
        // de criar a conta. `null` continua `null` (o QR PIX segue as regras de
        // sempre); só um `[]` explícito esconde o QR.
        // A chamada é repetida (e não extraída para uma variável) de propósito:
        // o guard de varredura de `quoteTotals.test.ts` procura as colunas
        // DENTRO de cada literal de payload. Um `...paymentColumns` some da
        // varredura e o guard passa a dar falsa segurança — o mesmo motivo pelo
        // qual `buildQuoteTotalsColumns` também aparece duas vezes aqui.
        ...buildQuotePaymentColumns({
          paymentMethods: guestQuote.payment_methods,
          paymentTerms: guestQuote.payment_terms,
          reference: guestQuote.reference,
        }),
        // Rascunho de visitante não tem seletor de modelo (o card fica travado
        // no grátis): null = segue o padrão da empresa que importou.
        pdf_template: null,
        title: safeTitle,
      };

      const operationId = addToOfflineQueue('create_quote', {
        quote: { ...quotePayload, company_id: resolvedCompanyId },
        items: itemsData,
        userId,
        companyId: resolvedCompanyId,
        generalPhotos: [],
      });

      const cachedQuotes = getCachedQuotes(userId) || [];
      const tempQuote = {
        id: operationId,
        ...quotePayload,
        user_id: userId,
        company_id: resolvedCompanyId,
        created_at: guestQuote.created_at || new Date().toISOString(),
        updated_at: new Date().toISOString(),
        _offline_pending: true,
        _offline_items: itemsData,
        _offline_photos: [],
      };
      cachedQuotes.unshift(tempQuote);
      cacheQuotes(userId, cachedQuotes);

      clearLocal();
      window.dispatchEvent(new CustomEvent('offline-queue-updated'));
      window.dispatchEvent(new CustomEvent('quotes-updated'));
      window.dispatchEvent(new CustomEvent('quote-created-offline'));

      toast.success('Seu orçamento de visitante foi salvo localmente! Será sincronizado quando houver internet.', {
        icon: '📴',
      });
      return true;
    } catch (err) {
      console.error('[importGuestQuote] Error converting guest quote to offline queue:', err);
      return false;
    }
  };

  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return convertGuestQuoteToOffline();
  }

  // Compute the next sequence_id from profile.lifetime_quotes_created so the
  // imported quote gets the ordinal a logged-in save would have produced.
  let nextSequenceId: number | null = null;
  let currentLifetimeCount = 0;
  try {
    const { data: profileData, error: profileError } = await supabase
      .from('profiles')
      .select('lifetime_quotes_created')
      .eq('id', userId)
      .maybeSingle();

    if (!profileError && profileData) {
      currentLifetimeCount = profileData.lifetime_quotes_created ?? 0;
      nextSequenceId = currentLifetimeCount + 1;
    }
  } catch (e) {
    console.warn('[importGuestQuote] Sequence ID lookup failed:', e);
  }

  const fullQuotePayload = {
    user_id: userId,
    company_id: resolvedCompanyId,
    client_name: safeClientName,
    status: safeStatus,
    document_type: guestQuote.document_type || 'orcamento',
    validity_days: guestQuote.validity_days || null,
    ...buildQuoteWarrantyColumns(guestQuote),
    ...buildQuoteHeadingColumns({
      subtitle: guestQuote.subtitle ?? '',
      issuePlace: guestQuote.issue_place ?? '',
    }),
    ...buildQuoteExtraFieldsColumns({ extraFields: quoteExtraFieldsFromRow(guestQuote) }),
    notes: guestQuote.notes || null,
    terms_conditions: guestQuote.terms_conditions || null,
    ...buildQuoteTotalsColumns({
      items: guestQuote.items || [],
      shipping: guestQuote.shipping ?? 0,
      discount: guestQuote.discount ?? 0,
      discountType: guestQuote.discount_type ?? 'value',
    }),
    ...buildQuotePaymentColumns({
      paymentMethods: guestQuote.payment_methods,
      paymentTerms: guestQuote.payment_terms,
      reference: guestQuote.reference,
    }),
    // Rascunho de visitante não tem seletor de modelo: null = padrão da empresa.
    pdf_template: null,
    title: safeTitle,
    sequence_id: nextSequenceId,
  };

  const minimalQuotePayload = {
    user_id: userId,
    company_id: resolvedCompanyId,
    client_name: safeClientName,
    status: safeStatus,
    validity_days: guestQuote.validity_days || null,
    ...buildQuoteWarrantyColumns(guestQuote),
    notes: guestQuote.notes || null,
    total: guestQuote.total || 0,
  };

  try {
    let newQuoteId: string | null = null;

    {
      const { data, error } = await supabase
        .from('quotes')
        .insert(fullQuotePayload)
        .select('id')
        .single();

      if (!error && data?.id) {
        newQuoteId = data.id as string;
      } else {
        const message = (error as { message?: string } | null)?.message;
        const code = (error as { code?: string } | null)?.code;
        const shouldRetryMinimal = !!message && /column .* does not exist/i.test(message);

        console.error('[importGuestQuote] Error inserting quote (full payload):', { code, message });

        if (!shouldRetryMinimal) {
          if ((typeof navigator !== 'undefined' && !navigator.onLine) || message?.includes('fetch') || code === 'FETCH_ERROR') {
            return convertGuestQuoteToOffline();
          }
          // The user is logged in and clicked import, so an RLS rejection here
          // means we couldn't resolve a company they can write to. Keep the local
          // quote and tell them plainly, rather than failing silently.
          const isRLSError = code === '42501' || message?.includes('row-level security');
          if (isRLSError) {
            toast.error('Não foi possível importar: nenhuma empresa disponível para salvar o orçamento.');
            return false;
          }
          toast.error(`Erro ao importar orçamento${message ? `: ${message}` : ''}`);
          return false;
        }

        const { data: data2, error: error2 } = await supabase
          .from('quotes')
          .insert(minimalQuotePayload)
          .select('id')
          .single();

        if (error2 || !data2?.id) {
          const message2 = (error2 as { message?: string } | null)?.message;
          const code2 = (error2 as { code?: string } | null)?.code;
          console.error('[importGuestQuote] Error inserting quote (fallback):', { code: code2, message: message2 });
          if ((typeof navigator !== 'undefined' && !navigator.onLine) || message2?.includes('fetch') || code2 === 'FETCH_ERROR') {
            return convertGuestQuoteToOffline();
          }
          toast.error(`Erro ao importar orçamento${message2 ? `: ${message2}` : ''}`);
          return false;
        }

        newQuoteId = data2.id as string;
      }
    }

    // Items (full payload first, minimal fallback on schema mismatch).
    const fullItemsPayload = (guestQuote.items || []).map((item, index) => {
      let discountType: 'value' | 'percent' | null = null;
      if (item.discount_type) {
        const dt = item.discount_type.toLowerCase();
        if (dt === 'percent' || dt === 'percentage' || dt === '%') discountType = 'percent';
        else if (dt === 'value' || dt === 'r$') discountType = 'value';
      }

      let itemType: 'service' | 'product' | null = null;
      if (item.itemType) {
        const it = item.itemType.toLowerCase();
        if (it === 'service' || it === 'servico' || it === 'serviço') itemType = 'service';
        else if (it === 'product' || it === 'produto') itemType = 'product';
      }

      return {
        quote_id: newQuoteId,
        product_id: item.product_id || null,
        name: item.name || 'Item sem nome',
        description: item.description || null,
        quantity: item.quantity || 1,
        unit_price: item.unit_price || 0,
        // Visitante não tem catálogo, então nunca há custo para congelar aqui —
        // explícito para deixar claro que é ausência de dado, não esquecimento.
        unit_cost: null,
        // Visitante não tem financeiro, então nunca há despesa cobrável a
        // vincular — explícito pelo mesmo motivo do custo acima.
        finance_entry_id: null,
        discount: item.discount || 0,
        discount_type: discountType,
        item_type: itemType,
        unit: item.unit || null,
        // Mesma nota do payload offline acima: `GuestQuoteItem` não tem 2/3.
        image_url: item.image_url || null,
        total: item.total || 0,
        position: index,
      };
    });

    const minimalItemsPayload = (guestQuote.items || []).map((item) => ({
      quote_id: newQuoteId,
      name: item.name || 'Item sem nome',
      description: item.description || null,
      quantity: item.quantity || 1,
      unit_price: item.unit_price || 0,
      total: item.total || 0,
    }));

    let itemsError: { message?: string; code?: string } | null = null;
    {
      const { error } = await supabase.from('quote_items').insert(fullItemsPayload);
      itemsError = error;
    }

    if (itemsError) {
      const message = itemsError.message;
      const shouldRetryMinimal = !!message && /column .* does not exist/i.test(message);

      if (shouldRetryMinimal) {
        const { error: error2 } = await supabase.from('quote_items').insert(minimalItemsPayload);
        if (error2) {
          console.error('[importGuestQuote] Error inserting items (fallback):', error2);
          await supabase.from('quotes').delete().eq('id', newQuoteId);
          toast.error(`Erro ao importar itens do orçamento${error2.message ? `: ${error2.message}` : ''}`);
          return false;
        }
      } else {
        console.error('[importGuestQuote] Error inserting quote items:', itemsError);
        await supabase.from('quotes').delete().eq('id', newQuoteId);
        toast.error(`Erro ao importar itens do orçamento${message ? `: ${message}` : ''}`);
        return false;
      }
    }

    // Record the import through the same atomic sequence + monthly quota path
    // used by regular quote creation.
    if (nextSequenceId !== null) {
      const { error: bumpError } = await supabase.rpc('record_quote_creation', {
        p_period_start: getQuotePeriodStart(),
        p_count_toward_quota: true,
        p_sequence_id: nextSequenceId,
      });
      if (bumpError) {
        console.warn('[importGuestQuote] Failed to record quote creation:', bumpError);
      } else {
        window.dispatchEvent(new CustomEvent('quote-quota-updated'));
      }
    }

    clearLocal();
    window.dispatchEvent(new CustomEvent('quotes-updated'));
    toast.success('Seu orçamento de visitante foi importado para a sua conta!');
    return true;
  } catch (e) {
    const err = e as { message?: string; name?: string };
    console.error('[importGuestQuote] Unexpected error:', e);
    if ((typeof navigator !== 'undefined' && !navigator.onLine) || err?.message?.includes('fetch') || err?.name === 'TypeError') {
      return convertGuestQuoteToOffline();
    }
    toast.error('Erro ao processar orçamento de visitante.');
    return false;
  }
}
